import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { PostCard } from '@/components/social/PostCard';
import { TopNavigation } from '@/components/layout/TopNavigation';
import MessageSquareTextIcon from '@/assets/icons/message-square-text.svg';
import BeltIcon from '@/assets/images/icons/belt.png';
import { useI18n } from '@/contexts/I18nContext';

interface UserPostsProps {
  userId: string;
  isOwnProfile: boolean;
}

export const UserPosts: React.FC<UserPostsProps> = ({ userId, isOwnProfile }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const { t } = useI18n();

  console.log('UserPosts component initialized with:', { userId, isOwnProfile, postsLength: posts.length });

  useEffect(() => {
    console.log('useEffect triggered with userId:', userId);
    if (userId && !isFetching) {
      fetchPosts();
    }
  }, [userId]);

  useEffect(() => {
    console.log('Component re-rendered. Current state:', {
      userId,
      postsCount: posts.length,
      loading,
      isFetching
    });
  });

  const fetchPosts = async () => {
    if (!userId || isFetching) return;

    console.log('Starting fetchPosts...');
    console.log('Current posts state before fetch:', posts.length, posts.map(p => p.id));
    
    setIsFetching(true);
    setLoading(true);
    
    // Limpar posts existentes antes da busca
    setPosts([]);
    
    try {
      // First, fetch posts for the specific user
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (postsError) {
        console.error('Error fetching posts:', postsError);
        setLoading(false);
        return;
      }

      if (!postsData || postsData.length === 0) {
        setPosts([]);
        setLoading(false);
        return;
      }

      // Then, fetch the author profiles with academy names
      const userIds = [...new Set(postsData.map(post => post.user_id))];
      
      // Filter out any invalid user IDs to prevent 406 errors
      const validUserIds = userIds.filter(id => id && id.length > 0);
      
      if (validUserIds.length === 0) {
        setPosts([]);
        setLoading(false);
        return;
      }
      
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          id, 
          full_name, 
          username, 
          profile_picture_url, 
          belt_rank,
          academies!profiles_main_academy_id_fkey(name)
        `)
        .in('id', validUserIds);
      
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        console.error('Error details:', {
          code: profilesError.code,
          message: profilesError.message,
          details: profilesError.details
        });
        console.error('User IDs being queried:', validUserIds);
        
        // If it's a 406 error, it means some profiles are not accessible due to RLS policies
        // We'll continue with the posts we can display
        if (profilesError.code === '406') {
          console.warn('Some profiles not accessible due to RLS policies, continuing with available data');
          // Set empty profiles map so we'll use fallback values
          const profilesMap = new Map();
          const transformedPosts = postsData.map(post => ({
            ...post,
            author: {
              id: post.user_id,
              full_name: 'Unknown User',
              username: 'unknown',
              profile_picture_url: null,
              belt_rank: 'white',
              academy_name: null
            },
            author_id: post.user_id
          }));
          setPosts(transformedPosts);
          setLoading(false);
          return;
        }
        
        setLoading(false);
        return;
      }

      // Create a map of profiles by id
      const profilesMap = new Map(profilesData?.map(profile => [profile.id, profile]) || []);

      // Transform the data to match the expected PostCard format
      // Filter out posts with non-existent profiles to prevent errors
      const transformedPosts = postsData
        .filter(post => {
          const profile = profilesMap.get(post.user_id);
          return profile && profile.username; // Only include posts with valid profiles
        })
        .map(post => {
          const profile = profilesMap.get(post.user_id);
          return {
            ...post,
            author: {
              id: post.user_id,
              full_name: profile?.full_name || 'Unknown User',
              username: profile?.username || 'unknown',
              profile_picture_url: profile?.profile_picture_url || null,
              belt_rank: profile?.belt_rank || 'white',
              academy_name: profile?.academies?.name || null
            },
            author_id: post.user_id
          };
        });
      setPosts(transformedPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDeletePost = async (postId: string) => {
    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId);

      if (error) {
        console.error('Error deleting post:', error);
      } else {
        // Remove the post from the local state
        setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  if (loading) {
    return (
      <>
        <TopNavigation variant="subtle" />
        <div className="min-h-screen bg-[#160C0C] text-white">
          <div className="pt-16">
            <div className="flex items-center justify-between p-4 border-b border-[#281B1B]">
              <button onClick={handleBack} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">{t('recognitions.back')}</span>
              </button>
              <h1 className="text-lg font-semibold">{t('profile.posts')}</h1>
              <div className="w-20"></div>
            </div>
            <div className="p-4">
              <div className="text-center py-8">
                <div className="text-white/60">{t('posts.loading_posts')}</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <TopNavigation variant="subtle" />
      <div className="min-h-screen bg-[#160C0C] text-white">
        
        {/* Header */}
        <div className="pt-16">
          <div className="flex items-center justify-between p-4 border-b border-[#281B1B]">
            <button onClick={handleBack} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">{t('recognitions.back')}</span>
            </button>
            <h1 className="text-lg font-semibold">{t('profile.posts')}</h1>
            <div className="w-20"></div>
          </div>

          {/* Content */}
          <div className="p-4">
            {posts.length > 0 ? (
              <div className="space-y-0">
                {posts.map((post, idx) => (
                  <div key={post.id}>
                    <PostCard post={post} onDelete={handleDeletePost} />
                    {/* Divider between posts, except the last */}
                    {idx < posts.length - 1 && (
                      <div className="relative flex items-center my-2">
                        <div className="flex-grow border-t border-[#332233]" />
                        <img src={BeltIcon} alt="Belt" className="h-5 w-7 mx-2" />
                        <div className="flex-grow border-t border-[#332233]" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
                {/* Empty State Icon */}
                <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                  <img 
                    src={MessageSquareTextIcon} 
                    alt="Posts" 
                    className="w-10 h-10 text-white/40"
                    style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
                  />
                </div>
                
                {/* Empty State Title */}
                <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                  {t('posts.no_posts_yet')}
                </h3>
                
                {/* Empty State Subtitle */}
                <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                  {isOwnProfile 
                    ? t('posts.share_news_community')
                    : t('posts.user_hasnt_shared')
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}; 